import { z } from 'zod';

export const MY_ACCOUNT_SCOPES = [
  'read:me:authentication_methods',
  'create:me:authentication_methods',
  'delete:me:authentication_methods',
].join(' ');

const authenticationMethodSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  confirmed: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  name: z.string().optional(),
});
const authenticationMethodsResponseSchema = z.object({
  authentication_methods: z.array(authenticationMethodSchema),
});

export type AuthenticationMethod = z.infer<typeof authenticationMethodSchema>;

export type AuthenticationEnrollment = {
  id: string;
  type: string;
  auth_session: string;
  barcode_uri?: string;
  manual_input_code?: string;
  recovery_code?: string;
  location?: string;
};

export interface AuthenticationMethodFailure extends Error {
  status?: number;
  code?: string;
  retryAfterSeconds?: number;
}

function providerRetryAfter(value: string | null): number {
  const seconds = z.coerce.number().int().positive().safeParse(value);
  if (seconds.success) return Math.min(3600, seconds.data);
  const date = Date.parse(value || '');
  return Number.isFinite(date)
    ? Math.min(3600, Math.max(1, Math.ceil((date - Date.now()) / 1000)))
    : 60;
}

export function getMyAccountAudience(domain: string): string {
  const normalized = domain
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/$/, '');
  if (!normalized || !/^[a-z0-9.-]+$/i.test(normalized)) {
    throw new Error('Auth0 domain is unavailable');
  }
  return `https://${normalized}/me/`;
}

function methodsUrl(domain: string, suffix = ''): string {
  return `${getMyAccountAudience(domain)}v1/authentication-methods${suffix}`;
}

async function request<T>(
  url: string,
  token: string,
  init: RequestInit = {},
  inspectResponse?: (response: Response) => void,
): Promise<T> {
  const response = await fetch(url, {
    ...init,
    cache: 'no-store',
    redirect: 'error',
    headers: {
      authorization: `Bearer ${token}`,
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });
  if (!response.ok) {
    const parsed = z
      .object({
        message: z.string().optional(),
        error: z.string().optional(),
        code: z.string().optional(),
      })
      .safeParse(await response.json().catch(() => ({})));
    const body = parsed.success ? parsed.data : {};
    const error: AuthenticationMethodFailure = new Error(
      body.message || body.error || 'Unable to update security settings',
    );
    error.status = response.status;
    error.code = body.code || body.error;
    if (response.status === 429)
      error.retryAfterSeconds = providerRetryAfter(response.headers.get('retry-after'));
    throw error;
  }
  inspectResponse?.(response);
  if (response.status === 204) return undefined as T;
  return response.json();
}

const passkeyChallengeSchema = z.object({
  auth_session: z.string().min(1),
  authn_params_public_key: z
    .object({
      challenge: z.string().min(1),
      rp: z.object({ id: z.string().min(1), name: z.string().min(1) }),
      user: z.object({
        id: z.string().min(1),
        name: z.string(),
        displayName: z.string(),
      }),
      pubKeyCredParams: z.array(z.object({ type: z.literal('public-key'), alg: z.number() })),
      excludeCredentials: z
        .array(z.object({ id: z.string(), type: z.literal('public-key') }).passthrough())
        .optional(),
    })
    .passthrough(),
});

export function decodeBase64Url(value: string): ArrayBuffer {
  const bytes = Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), (c) =>
    c.charCodeAt(0),
  );
  return bytes.buffer;
}

export function encodeBase64Url(value: ArrayBuffer): string {
  return btoa(Array.from(new Uint8Array(value), (byte) => String.fromCharCode(byte)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function supportsPasskeys(): boolean {
  return Boolean(
    window.isSecureContext && window.PublicKeyCredential && navigator.credentials?.create,
  );
}

/** Keep verification on the user-context endpoint returned by Auth0, never an arbitrary URL. */
function enrollmentLocation(domain: string, location: string): string {
  const base = new URL(methodsUrl(domain));
  const target = new URL(location, base);
  const prefix = `${base.pathname}/`;
  if (
    target.origin !== base.origin ||
    target.username ||
    target.password ||
    target.search ||
    target.hash ||
    !target.pathname.startsWith(prefix) ||
    !target.pathname.slice(prefix.length) ||
    target.pathname.slice(prefix.length).includes('/')
  ) {
    throw new Error('Invalid authentication enrollment location');
  }
  return target.href;
}

export async function enrollPasskey(
  domain: string,
  token: string,
  signal?: AbortSignal,
): Promise<AuthenticationMethod> {
  if (!supportsPasskeys())
    throw new Error('Passkeys require a supported browser on a secure connection.');
  let location = '';
  // Passkey enrollment has no ID in the body. Auth0 supplies its verification resource in Location.
  const challenge = await request<unknown>(
    methodsUrl(domain),
    token,
    {
      method: 'POST',
      body: JSON.stringify({ type: 'passkey' }),
      signal,
    },
    (response) => {
      location = response.headers.get('location') || '';
    },
  );
  const parsed = passkeyChallengeSchema.parse(challenge);
  const verifyUrl = `${enrollmentLocation(domain, location)}/verify`;
  const options = parsed.authn_params_public_key;
  // SAFETY: Required creation fields are parsed above; binary fields are decoded here.
  // Optional WebAuthn extensions pass through for the browser to validate.
  const publicKey = {
    ...options,
    challenge: decodeBase64Url(options.challenge),
    user: { ...options.user, id: decodeBase64Url(options.user.id) },
    excludeCredentials: options.excludeCredentials?.map((item) => ({
      ...item,
      id: decodeBase64Url(item.id),
    })),
  } as PublicKeyCredentialCreationOptions;
  // SAFETY: This request contains only publicKey, so the browser returns a PublicKeyCredential or null.
  const credential = (await navigator.credentials.create({
    publicKey,
    signal,
  })) as PublicKeyCredential | null;
  if (!credential) throw new DOMException('Passkey creation cancelled', 'NotAllowedError');
  signal?.throwIfAborted();
  // SAFETY: credentials.create with publicKey returns an attestation, never a login assertion.
  const response = credential.response as AuthenticatorAttestationResponse;
  const method = await request<unknown>(verifyUrl, token, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      auth_session: parsed.auth_session,
      authn_response: {
        id: credential.id,
        rawId: encodeBase64Url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment ?? undefined,
        response: {
          attestationObject: encodeBase64Url(response.attestationObject),
          clientDataJSON: encodeBase64Url(response.clientDataJSON),
        },
        clientExtensionResults: credential.getClientExtensionResults(),
      },
    }),
  });
  return (
    authenticationMethodSchema
      // Primary passkeys omit the MFA-only confirmed flag in Auth0's documented response.
      // Successful verification confirms registration; an explicit false remains invalid.
      .extend({ type: z.literal('passkey'), confirmed: z.literal(true).optional() })
      .parse(method)
  );
}

export async function listAuthenticationMethods(
  domain: string,
  token: string,
): Promise<AuthenticationMethod[]> {
  const response = await request<unknown>(methodsUrl(domain), token);
  return authenticationMethodsResponseSchema.parse(response).authentication_methods;
}

export async function startAuthenticationMethod(
  domain: string,
  token: string,
  type: 'totp' | 'recovery-code',
) {
  const enrollment = await request<Omit<AuthenticationEnrollment, 'type'> & { type?: string }>(
    methodsUrl(domain),
    token,
    {
      method: 'POST',
      body: JSON.stringify({ type }),
    },
  );
  // SAFETY: Auth0's enrollment response supplies the fields; the requested factor supplies its type.
  return { ...enrollment, type } as AuthenticationEnrollment;
}

export function verifyAuthenticationMethod(
  domain: string,
  token: string,
  enrollment: AuthenticationEnrollment,
  otpCode?: string,
) {
  const body: Record<string, string> = {
    auth_session: enrollment.auth_session,
  };
  if (otpCode) body.otp_code = otpCode;
  return request<AuthenticationMethod>(
    methodsUrl(domain, `/${encodeURIComponent(enrollment.id)}/verify`),
    token,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function deleteAuthenticationMethod(domain: string, token: string, methodId: string) {
  return request<void>(methodsUrl(domain, `/${encodeURIComponent(methodId)}`), token, {
    method: 'DELETE',
  });
}

export type SecurityAuthorization = {
  token: string;
  subject: string;
  email: string;
  expiresAt: number;
};
